import { SITE_YANDEX_METRIKA, SITE_YANDEX_METRIKA_COUNTER } from '@/config/site'
import { apiService } from '@/services/Api'
import { userLogin } from '@/services/Api/actions/auth'
import { AuthTokenResponse } from '@/services/Api/generated'
import { isAuthTokenResponseSuccess } from '@/services/Api/utils/authTokenResponseChecker'
import { Validator } from '@/services/Validator'
import { ERRORS_CODE } from '@/services/Validator/errors'
import {
  acceptPolicyValidator,
  confirmCodeValidator,
  emailValidator,
  passwordValidator,
  phoneValidator,
  requiredFieldValidator,
  retryPasswordValidator,
} from '@/services/Validator/utils/rules'
import { prepareApiErrors } from '@/services/Validator/utils/validate'
import { marketingStorage } from '@/store/MarketingStorage'
import { getPhoneNumber } from '@/utils/format'
import { makeAutoObservable, reaction, runInAction } from 'mobx'
import * as Yup from 'yup'
import { User, userStore } from '../User'

export enum SCREEN_LIST {
  LOGIN = 'LOGIN',
  REGISTRATION = 'REGISTRATION',
  REGISTRATION_SUCCESS = 'REGISTRATION_SUCCESS',
  EMAIL_NOT_VERIFIED = 'EMAIL_NOT_VERIFIED',
  PASSWORD_RESET_REQUEST = 'PASSWORD_RESET_REQUEST',
  PASSWORD_RESET_CONFIRM = 'PASSWORD_CONFIRM_RESET',
  PASSWORD_RESET = 'PASSWORD_RESET',
}

export const TabsOptions = [
  { value: SCREEN_LIST.REGISTRATION, label: 'Регистрация' },
  { value: SCREEN_LIST.LOGIN, label: 'Вход' },
]

export function isScreenList(name: string): name is SCREEN_LIST {
  return name in SCREEN_LIST
}

const registrationFormSchema = Yup.object().shape({
  name: requiredFieldValidator,
  phone: phoneValidator,
  email: emailValidator,
  password: passwordValidator,
  acceptPolicy: acceptPolicyValidator,
})

const loginFormSchema = Yup.object().shape({
  email: emailValidator,
  password: requiredFieldValidator,
})

const passwordResetRequestFormSchema = Yup.object().shape({
  email: emailValidator,
})

const confirmResetPasswordFormSchema = Yup.object().shape({
  code: confirmCodeValidator,
})

const resetPasswordFormSchema = Yup.object().shape({
  newPassword: passwordValidator,
  retryNewPassword: retryPasswordValidator('newPassword'),
})

export class AuthStore {
  form = {
    name: '',
    phone: '',
    email: '',
    password: '',
    acceptPolicy: false,
  }

  loading: boolean = false

  confirmResetPasswordCode: string = ''
  confirmResetPasswordError: boolean = false
  confirmResetToken: string = ''

  screen: SCREEN_LIST = SCREEN_LIST.REGISTRATION

  newPassword: string = ''
  retryNewPassword: string = ''

  loadingPage: boolean = false

  private validators = {
    [SCREEN_LIST.REGISTRATION]: new Validator(registrationFormSchema),
    [SCREEN_LIST.LOGIN]: new Validator(loginFormSchema),
    [SCREEN_LIST.PASSWORD_RESET_REQUEST]: new Validator(
      passwordResetRequestFormSchema
    ),
    [SCREEN_LIST.PASSWORD_RESET_CONFIRM]: new Validator(
      confirmResetPasswordFormSchema
    ),
    [SCREEN_LIST.PASSWORD_RESET]: new Validator(resetPasswordFormSchema),
  }

  get validator() {
    return this.validators[this.screen]
  }

  constructor(private _userStore: User) {
    makeAutoObservable(this, {}, { autoBind: true })
    reaction(
      () => this.screen,
      () => {
        if (this.validator != null) {
          this.validator.validateFilled(this.form)
        }
      }
    )
  }

  async registration() {
    if (!this.validator.validateAll(this.form)) {
      return { status: false }
    }

    this.startLoading()
    try {
      const response = await apiService.user.registration({
        registrationRequest: {
          email: this.form.email,
          name: this.form.name,
          password: this.form.password,
          phone: getPhoneNumber(this.form.phone),
          utmClientId: marketingStorage.getClientId() ?? undefined,
          promocode: marketingStorage.getPromocode() ?? undefined,
        },
      })
      if (response.status) {
        marketingStorage.clearClientId()
        marketingStorage.clearPromocode()
        if (SITE_YANDEX_METRIKA) {
          ym(SITE_YANDEX_METRIKA_COUNTER, 'reachGoal', 'registration_success')
        }
        this.setLoadingPage(true)
        this.setScreen(SCREEN_LIST.REGISTRATION_SUCCESS)
        this.setLoadingPage(false)
      }
    } catch (error) {
      const response = await error.response.json()
      const errors = response?.errors ?? {}
      this.validator.setErrors(errors)
      return error
    } finally {
      this.stopLoading()
    }

    return { status: false }
  }

  async getUser() {
    const response = await apiService.user.getContactData()
    if (response.status) {
      this._userStore.setData(response.data.user)
    }
  }

  async login(): Promise<
    { status: true; data: AuthTokenResponse } | { status: false }
  > {
    if (!this.validator.validateAll(this.form)) {
      return { status: false }
    }
    this.startLoading()
    try {
      const utmClientId = marketingStorage.getClientId()
      const promocode = marketingStorage.getPromocode()

      const response = await userLogin(
        this.form.email,
        this.form.password,
        utmClientId,
        promocode
      )

      if (isAuthTokenResponseSuccess(response)) {
        marketingStorage.clearClientId()
        marketingStorage.clearPromocode()
        return { status: true, data: response.data }
      } else {
        const errors = response.errors

        if (errors._common === ERRORS_CODE.EMAIL_NOT_VERIFIED) {
          this.setScreen(SCREEN_LIST.EMAIL_NOT_VERIFIED)
        }

        this.validator.setErrors(errors)

        return { status: false }
      }
    } catch (error) {
      return { status: false }
    } finally {
      this.stopLoading()
    }
  }

  async passwordResetRequest() {
    if (this.validator.validateAll(this.form)) {
      this.startLoading()
      try {
        const response = await apiService.user.forgetPassword({
          forgetPasswordRequest: {
            email: this.form.email,
          },
        })
        if (response.status) {
          this.setScreen(SCREEN_LIST.PASSWORD_RESET_CONFIRM)
        }
      } catch (error) {
        this.validator.setErrors(prepareApiErrors(error.response.errors))
      } finally {
        this.stopLoading()
      }
    }
  }

  async setConfirmResetPasswordCode(value: string) {
    this.confirmResetPasswordCode = value

    if (value.length !== 6) {
      return
    }

    try {
      this.startLoading()
      const response = await apiService.user.resetPasswordToken({
        resetPasswordTokenRequest: {
          code: value,
        },
      })
      if (response.status) {
        this.confirmResetToken = response.data.token
        this.setScreen(SCREEN_LIST.PASSWORD_RESET)
      }
    } catch (error) {
      this.confirmResetPasswordError = true
    } finally {
      this.stopLoading()
    }
  }

  async saveNewPassword() {
    if (
      this.validator.validateAll({
        newPassword: this.newPassword,
        retryNewPassword: this.retryNewPassword,
      })
    ) {
      this.startLoading()
      const response = await apiService.user.newPassword({
        newPasswordRequest: {
          token: this.confirmResetToken,
          password: this.newPassword,
          passwordConfirmation: this.retryNewPassword,
        },
      })
      this.setPassword('')
      runInAction(() => {
        this.confirmResetPasswordCode = ''
      })
      this.validators[SCREEN_LIST.LOGIN].setErrors({ _common: null })
      this.stopLoading()
      return response
    } else {
      return { status: false }
    }
  }

  goToLogin() {
    this.screen = SCREEN_LIST.LOGIN
  }

  goToResetPassword() {
    this.screen = SCREEN_LIST.PASSWORD_RESET_REQUEST
  }

  goToRegistration() {
    this.screen = SCREEN_LIST.REGISTRATION
  }

  setScreen(screen: SCREEN_LIST) {
    this.screen = screen
  }

  setLoadingPage(value: boolean) {
    this.loadingPage = value
  }

  startLoading() {
    this.loading = true
  }

  stopLoading() {
    this.loading = false
  }

  setName(name: string) {
    this.validator.validateAt('name', name)
    this.form.name = name
  }

  setPhone(phone: string) {
    this.validator.validateAt('phone', phone)
    this.form.phone = phone
  }

  setEmail(email: string) {
    this.validator.validateAt('email', email)
    this.form.email = email
  }

  setPassword(password: string) {
    this.validator.validateAt('password', password)
    this.form.password = password
  }

  setAcceptPolicy(acceptPolicy: boolean) {
    this.validator.validateAt('acceptPolicy', acceptPolicy)
    this.form.acceptPolicy = acceptPolicy
  }

  setNewPassword(value: string) {
    this.newPassword = value
  }

  setRetryNewPassword(value: string) {
    this.retryNewPassword = value
  }

  resetData() {
    this.form = {
      name: '',
      phone: '',
      email: '',
      password: '',
      acceptPolicy: false,
    }

    this.loading = false

    this.confirmResetPasswordCode = ''
    this.confirmResetPasswordError = false
    this.confirmResetToken = ''

    this.screen = SCREEN_LIST.LOGIN

    this.newPassword = ''
    this.retryNewPassword = ''
  }
}

export const authStore = new AuthStore(userStore)
